import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useInfiniteQuery } from '@tanstack/react-query';

import { isProblemError } from '../../api/client';
import { useGetMe } from '../../api/generated/identity/identity';
import {
  getListSubscriptionsQueryKey,
  listSubscriptions,
  useCancelSubscription,
  useCreatePrice,
  useCreateSubscription,
  useGetDunningPolicy,
  useListPrices,
  usePutDunningPolicy,
  useUpdatePrice,
} from '../../api/generated/ledger/ledger';
import type { DunningPolicy, Price, PriceRecurrence, Subscription } from '../../api/generated/model';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import { Field } from '../../design/primitives/Field';
import { formatMoney } from '../billing/money';
import { isReauthRequired, useReauthGate, type ReauthGate } from '../reauth/ReauthGate';
import styles from './pricing.module.css';

const RECURRENCES: readonly PriceRecurrence[] = ['one_time', 'monthly'];

/**
 * O teto que o contrato permite pedir de uma vez (`limit`, `maximum: 200`), e
 * que `normalizaLimite` (`api/internal/ledger/service.go`) honra sem reduzir.
 * Pedir o maximo nao substitui seguir o cursor — ele so faz o quadro de
 * assinaturas de uma escola caber em menos idas e voltas.
 */
const SUBSCRIPTIONS_PAGE_SIZE = 200;

/**
 * Catalogo de precos, politica de inadimplencia e assinaturas.
 *
 * **Esta e a tela que tira o preco do placeholder.** Enquanto ela nao existia,
 * o preco nascia provisorio pela §15.2, o checkout respondia 503 e o boot em
 * producao falhava — sem nenhum caminho pela interface para sair disso. A
 * pendencia so tinha saida por migration escrita a mao, o que e o mesmo que
 * dizer que ela nao tinha saida.
 *
 * As operacoes que ratificam exigem credencial reconfirmada, e o portao de
 * reautenticacao nasce uma vez para a tela inteira: assim o formulario ja
 * preenchido volta a rodar de onde parou em vez de se perder num novo login.
 */
export function PricingPage() {
  const { t } = useTranslation();
  const meQuery = useGetMe();
  const me = meQuery.data;
  const gate = useReauthGate();

  const pricesQuery = useListPrices({ query: { enabled: !!me } });
  const policyQuery = useGetDunningPolicy({ query: { enabled: !!me } });
  /**
   * A LISTA DE ASSINATURAS SEGUE O CURSOR, e nao seguia.
   *
   * `listSubscriptions` declara `cursor` e `limit` no contrato e o ledger trunca
   * em 50 por chamada (`defaultPageSize`, `api/internal/ledger/service.go`),
   * devolvendo `nextCursor`. Pedindo sem cursor, da assinatura 51 em diante
   * ninguem alcancava — e alcancar aqui e o unico jeito de CANCELAR pela
   * interface. Numa tela que decide dinheiro recorrente, "nao aparece" e
   * "continua cobrando todo mes".
   */
  const subscriptionsQuery = useInfiniteQuery({
    queryKey: getListSubscriptionsQueryKey({ limit: SUBSCRIPTIONS_PAGE_SIZE }),
    queryFn: ({ pageParam, signal }) =>
      listSubscriptions(
        pageParam === undefined
          ? { limit: SUBSCRIPTIONS_PAGE_SIZE }
          : { limit: SUBSCRIPTIONS_PAGE_SIZE, cursor: pageParam },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!me,
  });

  const { hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage } =
    subscriptionsQuery;
  useEffect(() => {
    // `isFetchNextPageError` faz parte da condicao de parada: sem ele, a pagina
    // que falha no meio deixa `hasNextPage` verdadeiro e `isFetchingNextPage` de
    // volta em falso, o efeito dispara de novo e a tela entra em tempestade de
    // requisicoes contra o servidor que acabou de recusar.
    if (!hasNextPage || isFetchingNextPage || isFetchNextPageError) return;
    void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);

  if (!me) return null;

  const subscriptions = (subscriptionsQuery.data?.pages ?? []).flatMap((page) => page.items);
  // `isError` fala so da primeira pagina; a que falha no meio deixaria o quadro
  // truncado com a mesma cara de quadro completo.
  const subscriptionsFailed = subscriptionsQuery.isError || isFetchNextPageError;
  const subscriptionsLoading =
    !subscriptionsFailed && (subscriptionsQuery.data === undefined || hasNextPage);

  const prices = pricesQuery.data ?? [];
  const policy = policyQuery.data;
  const pendingRatification =
    prices.some((price) => price.provisional && price.active) || policy?.provisional === true;

  return (
    <div className={styles.page}>
      <h1>{t('pricing.title')}</h1>

      {gate.dialog}

      {/*
        O aviso diz o que **esta bloqueado agora**, e nao apenas que ha algo
        provisorio: sem a consequencia escrita, quem abre a tela nao sabe que o
        boot de producao depende dela.
      */}
      {pendingRatification ? (
        <Alert tone="warning" title={t('pricing.pendingTitle')}>
          {t('pricing.pendingDetail')}
        </Alert>
      ) : null}

      <section aria-label={t('pricing.prices')}>
        <h2>{t('pricing.prices')}</h2>
        {pricesQuery.isError ? <Alert tone="error">{t('error.loadFailed')}</Alert> : null}
        <ul className={styles.list}>
          {prices.map((price) => (
            <PriceCard
              key={price.id}
              price={price}
              locale={me.locale}
              gate={gate}
              onChanged={() => void pricesQuery.refetch()}
            />
          ))}
        </ul>
        <PriceCreator gate={gate} onCreated={() => void pricesQuery.refetch()} />
      </section>

      <DunningPolicyEditor
        policy={policy}
        failed={policyQuery.isError}
        locale={me.locale}
        gate={gate}
        onSaved={() => void policyQuery.refetch()}
      />

      <section aria-label={t('pricing.subscriptions.title')}>
        <h2>{t('pricing.subscriptions.title')}</h2>
        {/*
          Leitura que falhou NAO e "nenhuma assinatura". A afirmacao de vazio
          nesta secao e a mais cara da tela: quem le que nao ha assinatura para
          de procurar a que continua cobrando o aluno todo mes. E ela mentia por
          dois caminhos — o 500, que caia no mesmo ramo do vazio, e o cursor por
          seguir, que fazia o mesmo com quem tem mais de 50.
        */}
        {subscriptionsFailed ? (
          <Alert tone="error">{t('error.loadFailed')}</Alert>
        ) : subscriptionsLoading ? (
          <p className={styles.meta}>{t('app.loading')}</p>
        ) : subscriptions.length === 0 ? (
          <p className={styles.meta}>{t('pricing.subscriptions.empty')}</p>
        ) : (
          <ul className={styles.list}>
            {subscriptions.map((subscription) => (
              <SubscriptionCard
                key={subscription.id}
                subscription={subscription}
                onChanged={() => void subscriptionsQuery.refetch()}
              />
            ))}
          </ul>
        )}
        <SubscriptionCreator
          prices={prices}
          onCreated={() => void subscriptionsQuery.refetch()}
        />
      </section>
    </div>
  );
}

/**
 * Um preco do catalogo, com a ratificacao ao lado quando ele e provisorio.
 *
 * A regra que o formulario carrega, e que o servidor recusa com 422 se a tela
 * errar: `provisional` so vai de `true` para `false`, e **so junto de um
 * `amount` real**. Nao ha botao para reabrir um preco ja ratificado — isso
 * desligaria o gate de boot por dentro, que e justamente o que o campo sustenta.
 */
function PriceCard({
  price,
  locale,
  gate,
  onChanged,
}: {
  readonly price: Price;
  readonly locale: string;
  readonly gate: ReauthGate;
  readonly onChanged: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdatePrice();
  const [amountCents, setAmountCents] = useState('');

  const parsed = Number.parseInt(amountCents, 10);
  const validAmount = Number.isInteger(parsed) && parsed > 0;
  const unexpected = update.error !== null && !isReauthRequired(update.error);

  return (
    // O rotulo acessivel nomeia o preco, e nao o estado: "Provisorio" tambem e
    // o texto da marca, e uma asercao solta sobre ele casaria com qualquer
    // linha da lista.
    <li
      className={`${styles.card} ${price.provisional ? styles.provisional : ''}`}
      aria-label={price.label}
    >
      <p>
        <strong>{price.label}</strong> · {formatMoney(price.amount, locale)} ·{' '}
        <span>{t(`pricing.recurrence.${price.recurrence}`)}</span>
      </p>
      <p className={styles.meta}>
        {price.active ? t('pricing.active') : t('pricing.inactive')}
        {price.provisional ? ` · ${t('pricing.provisionalTag')}` : ''}
      </p>

      {unexpected ? <Alert tone="error">{t('error.unexpected')}</Alert> : null}

      {price.provisional ? (
        <div className={styles.row}>
          <p className={styles.meta}>{t('pricing.ratifyExplained')}</p>
          <Field
            label={t('pricing.amountCents')}
            type="number"
            required
            value={amountCents}
            onChange={(event) => setAmountCents(event.target.value)}
            hint={
              validAmount
                ? formatMoney({ amountCents: parsed, currency: 'BRL' }, locale)
                : t('pricing.amountHint')
            }
          />
          <Button
            variant="primary"
            disabled={!validAmount}
            loading={update.isPending}
            onClick={() =>
              gate.run((onReauthRequired) =>
                update.mutate(
                  {
                    priceId: price.id,
                    data: {
                      provisional: false,
                      amount: { amountCents: parsed, currency: 'BRL' },
                    },
                  },
                  { onSuccess: onChanged, onError: onReauthRequired },
                ),
              )
            }
          >
            {t('pricing.ratify')}
          </Button>
        </div>
      ) : (
        <div className={styles.row}>
          <Button
            variant="secondary"
            loading={update.isPending}
            onClick={() =>
              gate.run((onReauthRequired) =>
                update.mutate(
                  { priceId: price.id, data: { active: !price.active } },
                  { onSuccess: onChanged, onError: onReauthRequired },
                ),
              )
            }
          >
            {price.active ? t('pricing.deactivate') : t('pricing.activate')}
          </Button>
        </div>
      )}
    </li>
  );
}

/** Preco novo. Nasce definitivo: placeholder e semeadura, e nao criacao pela tela. */
function PriceCreator({
  gate,
  onCreated,
}: {
  readonly gate: ReauthGate;
  readonly onCreated: () => void;
}) {
  const { t } = useTranslation();
  const create = useCreatePrice();

  const [label, setLabel] = useState('');
  const [amountCents, setAmountCents] = useState('');
  const [recurrence, setRecurrence] = useState<PriceRecurrence>('monthly');

  const parsed = Number.parseInt(amountCents, 10);
  const validAmount = Number.isInteger(parsed) && parsed > 0;
  const canSubmit = label.trim().length > 0 && validAmount;
  const unexpected = create.error !== null && !isReauthRequired(create.error);

  return (
    <form
      className={styles.form}
      aria-label={t('pricing.createPrice')}
      onSubmit={(event) => {
        event.preventDefault();
        if (!canSubmit) return;
        gate.run((onReauthRequired) =>
          create.mutate(
            {
              data: {
                label: label.trim(),
                amount: { amountCents: parsed, currency: 'BRL' },
                recurrence,
              },
            },
            {
              onSuccess: () => {
                setLabel('');
                setAmountCents('');
                onCreated();
              },
              onError: onReauthRequired,
            },
          ),
        );
      }}
    >
      <h3 className={styles.subheading}>{t('pricing.createPrice')}</h3>

      {unexpected ? <Alert tone="error">{t('error.unexpected')}</Alert> : null}

      <Field
        label={t('pricing.label')}
        required
        value={label}
        onChange={(event) => setLabel(event.target.value)}
      />
      <Field
        label={t('pricing.amountCents')}
        type="number"
        required
        value={amountCents}
        onChange={(event) => setAmountCents(event.target.value)}
        hint={t('pricing.amountHint')}
      />
      <label className={styles.selectLabel}>
        {t('pricing.recurrenceLabel')}
        <select
          value={recurrence}
          onChange={(event) => setRecurrence(event.target.value as PriceRecurrence)}
        >
          {RECURRENCES.map((option) => (
            <option key={option} value={option}>
              {t(`pricing.recurrence.${option}`)}
            </option>
          ))}
        </select>
      </label>

      <div>
        <Button variant="primary" type="submit" disabled={!canSubmit} loading={create.isPending}>
          {t('pricing.createPriceAction')}
        </Button>
      </div>
    </form>
  );
}

/**
 * Politica de inadimplencia.
 *
 * **Nulo em `blockAccessAfterDays` significa apurar sem bloquear** — o estado
 * provisorio conservador enquanto a §15.2 nao fecha. O campo tem uma caixa
 * propria para o nulo em vez de tratar vazio como zero: zero dias bloquearia no
 * dia do vencimento, que e o oposto do que o nulo diz.
 */
function DunningPolicyEditor({
  policy,
  failed,
  locale,
  gate,
  onSaved,
}: {
  readonly policy: DunningPolicy | undefined;
  /**
   * A leitura da politica falhou. Sem este ramo, o 500 chegava aqui como
   * `policy === undefined` — indistinguivel da primeira carga — e a secao
   * ficava com o titulo, a explicacao e mais NADA: nem formulario, nem motivo.
   * Quem viesse ratificar a politica (a §15.2 depende dela para o boot em
   * producao) encontrava uma secao muda e nenhuma acao possivel.
   *
   * O efeito nao para na secao: `pendingRatification`, uma tela acima, le
   * `policy?.provisional === true`. Com a leitura falhando esse termo vira
   * falso, e o aviso que diz que a subida em producao esta presa aqui perde
   * metade da propria condicao — guarda que enfraquece em silencio quando a
   * consulta que a alimenta cai e a mesma guarda-falso que o financeiro
   * administrativo ja carregava.
   */
  readonly failed: boolean;
  readonly locale: string;
  readonly gate: ReauthGate;
  readonly onSaved: () => void;
}) {
  const { t } = useTranslation();

  return (
    <section aria-label={t('pricing.dunning.title')}>
      <h2>{t('pricing.dunning.title')}</h2>
      <p className={styles.meta}>{t('pricing.dunning.explained')}</p>

      {failed ? <Alert tone="error">{t('error.loadFailed')}</Alert> : null}
      {policy?.provisional ? (
        <Alert tone="warning">{t('pricing.dunning.provisionalTag')}</Alert>
      ) : null}

      {/*
        O formulario so monta com a politica em maos, e parte dos valores dela —
        nao de zeros. Editar uma politica vigente a partir de campos vazios
        reescreveria o que ninguem quis mudar.

        A chave inclui `provisional` para que a ratificacao remonte o formulario
        com o que o servidor devolveu, em vez de manter o rascunho anterior.
      */}
      {policy ? (
        <DunningPolicyForm
          key={String(policy.provisional)}
          policy={policy}
          locale={locale}
          gate={gate}
          onSaved={onSaved}
        />
      ) : null}
    </section>
  );
}

function DunningPolicyForm({
  policy,
  locale,
  gate,
  onSaved,
}: {
  readonly policy: DunningPolicy;
  readonly locale: string;
  readonly gate: ReauthGate;
  readonly onSaved: () => void;
}) {
  const { t } = useTranslation();
  const save = usePutDunningPolicy();

  const [gracePeriodDays, setGracePeriodDays] = useState(() => String(policy.gracePeriodDays));
  const [blocks, setBlocks] = useState(() => policy.blockAccessAfterDays !== null);
  const [blockAfterDays, setBlockAfterDays] = useState(() =>
    policy.blockAccessAfterDays === null ? '' : String(policy.blockAccessAfterDays),
  );
  const [cancelsClasses, setCancelsClasses] = useState(() => policy.cancelsScheduledClasses);
  const [lateFeeCents, setLateFeeCents] = useState(() =>
    policy.lateFee === null ? '' : String(policy.lateFee.amountCents),
  );
  const [interestBasisPoints, setInterestBasisPoints] = useState(() =>
    policy.monthlyInterestBasisPoints === null ? '' : String(policy.monthlyInterestBasisPoints),
  );

  const grace = Number.parseInt(gracePeriodDays, 10);
  const blockAfter = Number.parseInt(blockAfterDays, 10);
  const validGrace = Number.isInteger(grace) && grace >= 0;
  const validBlock = !blocks || (Number.isInteger(blockAfter) && blockAfter >= grace);
  const canSubmit = validGrace && validBlock;
  const unexpected = save.error !== null && !isReauthRequired(save.error);

  const parsedFee = Number.parseInt(lateFeeCents, 10);
  const parsedInterest = Number.parseInt(interestBasisPoints, 10);

  return (
    <>
      {unexpected ? <Alert tone="error">{t('error.unexpected')}</Alert> : null}

      <form
        className={styles.form}
        aria-label={t('pricing.dunning.form')}
        onSubmit={(event) => {
          event.preventDefault();
          if (!canSubmit) return;
          gate.run((onReauthRequired) =>
            save.mutate(
              {
                data: {
                  gracePeriodDays: grace,
                  blockAccessAfterDays: blocks ? blockAfter : null,
                  cancelsScheduledClasses: cancelsClasses,
                  lateFee:
                    Number.isInteger(parsedFee) && parsedFee > 0
                      ? { amountCents: parsedFee, currency: 'BRL' }
                      : null,
                  monthlyInterestBasisPoints:
                    Number.isInteger(parsedInterest) && parsedInterest > 0 ? parsedInterest : null,
                },
              },
              { onSuccess: onSaved, onError: onReauthRequired },
            ),
          );
        }}
      >
        <Field
          label={t('pricing.dunning.gracePeriodDays')}
          type="number"
          required
          value={gracePeriodDays}
          onChange={(event) => setGracePeriodDays(event.target.value)}
        />

        <label className={styles.checkLabel}>
          <input
            type="checkbox"
            checked={blocks}
            onChange={(event) => setBlocks(event.target.checked)}
          />
          {t('pricing.dunning.blocks')}
        </label>

        {blocks ? (
          <Field
            label={t('pricing.dunning.blockAccessAfterDays')}
            type="number"
            required
            value={blockAfterDays}
            onChange={(event) => setBlockAfterDays(event.target.value)}
            error={validBlock ? undefined : t('pricing.dunning.blockBeforeGrace')}
          />
        ) : (
          <p className={styles.meta}>{t('pricing.dunning.blockNever')}</p>
        )}

        <label className={styles.checkLabel}>
          <input
            type="checkbox"
            checked={cancelsClasses}
            onChange={(event) => setCancelsClasses(event.target.checked)}
          />
          {t('pricing.dunning.cancelsScheduledClasses')}
        </label>

        <Field
          label={t('pricing.dunning.lateFeeCents')}
          type="number"
          value={lateFeeCents}
          onChange={(event) => setLateFeeCents(event.target.value)}
          hint={
            Number.isInteger(parsedFee) && parsedFee > 0
              ? formatMoney({ amountCents: parsedFee, currency: 'BRL' }, locale)
              : t('pricing.dunning.lateFeeHint')
          }
        />
        {/*
          Juros em pontos-base inteiros, pela mesma razao que dinheiro e
          inteiro: 1% ao mes sao 100 pontos-base, e nao 0,01 em ponto flutuante.
        */}
        <Field
          label={t('pricing.dunning.monthlyInterestBasisPoints')}
          type="number"
          value={interestBasisPoints}
          onChange={(event) => setInterestBasisPoints(event.target.value)}
          hint={t('pricing.dunning.interestHint')}
        />

        <div>
          <Button variant="primary" type="submit" disabled={!canSubmit} loading={save.isPending}>
            {t('pricing.dunning.save')}
          </Button>
        </div>
      </form>
    </>
  );
}

function SubscriptionCard({
  subscription,
  onChanged,
}: {
  readonly subscription: Subscription;
  readonly onChanged: () => void;
}) {
  const { t } = useTranslation();
  const cancel = useCancelSubscription();

  return (
    <li className={styles.card} aria-label={subscription.student.displayName}>
      <p>
        <strong>{subscription.student.displayName}</strong> ·{' '}
        <span>{t(`billing.subscriptionState.${subscription.state}`)}</span>
      </p>
      {subscription.state === 'cancelled' ? null : (
        <div className={styles.row}>
          <Button
            variant="danger"
            loading={cancel.isPending}
            onClick={() =>
              cancel.mutate({ subscriptionId: subscription.id }, { onSuccess: onChanged })
            }
          >
            {t('pricing.subscriptions.cancel')}
          </Button>
        </div>
      )}
    </li>
  );
}

/**
 * Assinatura nova.
 *
 * Assinar um preco provisorio prenderia o aluno a um centavo, e o servidor
 * recusa com 503. A tela diz isso em vez de mostrar "erro inesperado", porque a
 * saida existe e esta logo acima nesta mesma pagina.
 */
function SubscriptionCreator({
  prices,
  onCreated,
}: {
  readonly prices: readonly Price[];
  readonly onCreated: () => void;
}) {
  const { t } = useTranslation();
  const create = useCreateSubscription();

  const [studentId, setStudentId] = useState('');
  const [planId, setPlanId] = useState('');
  const [priceId, setPriceId] = useState('');

  const canSubmit =
    studentId.trim().length > 0 && planId.trim().length > 0 && priceId.trim().length > 0;
  const blockedByPlaceholder = isProblemError(create.error) && create.error.status === 503;
  const unexpected = create.error !== null && !blockedByPlaceholder;

  return (
    <form
      className={styles.form}
      aria-label={t('pricing.subscriptions.create')}
      onSubmit={(event) => {
        event.preventDefault();
        if (!canSubmit) return;
        create.mutate(
          { data: { studentId: studentId.trim(), planId: planId.trim(), priceId } },
          {
            onSuccess: () => {
              setStudentId('');
              setPlanId('');
              onCreated();
            },
          },
        );
      }}
    >
      <h3 className={styles.subheading}>{t('pricing.subscriptions.create')}</h3>

      {blockedByPlaceholder ? (
        <Alert tone="warning" title={t('pricing.subscriptions.blockedTitle')}>
          {t('pricing.subscriptions.blockedDetail')}
        </Alert>
      ) : null}
      {unexpected ? <Alert tone="error">{t('error.unexpected')}</Alert> : null}

      <Field
        label={t('pricing.subscriptions.student')}
        required
        value={studentId}
        onChange={(event) => setStudentId(event.target.value)}
      />
      <Field
        label={t('pricing.subscriptions.plan')}
        required
        value={planId}
        onChange={(event) => setPlanId(event.target.value)}
      />
      <label className={styles.selectLabel}>
        {t('pricing.subscriptions.price')}
        <select value={priceId} onChange={(event) => setPriceId(event.target.value)}>
          <option value="">{t('pricing.subscriptions.pricePlaceholder')}</option>
          {prices.map((price) => (
            <option key={price.id} value={price.id}>
              {price.label}
            </option>
          ))}
        </select>
      </label>

      <div>
        <Button variant="primary" type="submit" disabled={!canSubmit} loading={create.isPending}>
          {t('pricing.subscriptions.createAction')}
        </Button>
      </div>
    </form>
  );
}
